import { computed, makeObservable } from 'mobx'

import { SitePermitStatus } from '~/client/graph'
import DesktopEventsStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { IFacetCount } from '~/client/src/desktop/views/Logistics/components/LogisticsList/LogisticsListFacet.store'
import { UNASSIGNED_FILTER_OPTION } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import { AttachmentFileTypeList } from '~/client/src/shared/enums/FileType'
import {
  formsFilterTypes,
  getLogisticsFilterTypeCaption,
  LogisticsFilterType,
} from '~/client/src/shared/enums/LogisticsFilterType'
import { TagType } from '~/client/src/shared/enums/TagType'
import ILogisticItem from '~/client/src/shared/models/ILogisticItem'
import { ISourceMap } from '~/client/src/shared/stores/BaseLogisticsFilter.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import LogisticsFilterStore from '~/client/src/shared/stores/LogisticsFilter.store'
import UIFilterInfo from '~/client/src/shared/stores/substates/UIFilterInfo'
import { enumToList } from '~/client/src/shared/utils/converters'
import { LOCATION_SEPARATOR } from '~/client/src/shared/utils/usefulStrings'

import LogisticsStore from '../../../Logistics/Logistics.store'
import LogisticsListStore from '../../../LogisticsList/LogisticsList.store'
import LogisticsListFacetStore from '../../../LogisticsList/LogisticsListFacet.store'
import DesktopLogisticsFilterStore from '../../DesktopLogisticsFilter.store'

export default class LogisticsListFilterStore extends LogisticsFilterStore {
  public constructor(
    protected readonly eventsStore: DesktopEventsStore,
    protected readonly logisticsStore: LogisticsStore,
    protected readonly logisticsListStore: LogisticsListStore,
    protected readonly onShowChanged: (
      isShown: boolean,
      filterType: string,
    ) => void,
    protected readonly locationAttributesStore: LocationAttributesStore,
    protected readonly workflowTypesStore: WorkflowTypesStore,
    protected readonly tagsStore: TagsStore,
    protected readonly companiesStore: CompaniesStore,
    protected readonly workflowCategoriesStore: WorkflowCategoriesStore,
    protected readonly projectsStore: ProjectsStore,
    protected readonly sharedSitePermitStore: SharedSitePermitStore,
    protected readonly logisticsListFacetStore?: LogisticsListFacetStore,
    private readonly onFilterClickHandler?: () => void,
  ) {
    super(
      eventsStore,
      logisticsStore,
      onShowChanged,
      locationAttributesStore,
      workflowTypesStore,
      tagsStore,
      workflowCategoriesStore,
      projectsStore,
      sharedSitePermitStore,
    )
    makeObservable(this)
  }

  protected get availableInstances(): ILogisticItem[] {
    return this.logisticsStore.allLogistics
  }

  @computed
  public get filterStoresByTypeMap(): {
    [filterType: string]: DesktopLogisticsFilterStore
  } {
    const { appState } = this.eventsStore
    const map: { [filterType: string]: DesktopLogisticsFilterStore } = {}

    this.filterTypes.forEach(filterType => {
      map[filterType] = new DesktopLogisticsFilterStore(
        filterType,
        appState,
        () => this.sourceMapByFilterTypeMap[filterType],
        this.logisticsListStore,
        this.onShowChanged,
        this.fieldsMap(),
        getLogisticsFilterTypeCaption,
        this.getOptionName,
        option => this.getFacet(filterType, option.id)?.viewableCount || 0,
        option => this.getFacet(filterType, option.id)?.totalCount || 0,
        this.onFilterClickHandler,
      )
    })

    return map
  }

  private getFacet(
    filterType: LogisticsFilterType,
    optionId: string,
  ): IFacetCount {
    return this.logisticsListFacetStore.facets[filterType][optionId]
  }

  @computed
  protected get sourceMapByFilterTypeMap(): {
    [filterType: string]: ISourceMap
  } {
    return this.filterTypes.reduce((acc, filterType) => {
      acc[filterType] = this.getDefaultSourceMapByType(filterType)
      return acc
    }, {})
  }

  protected getDefaultSourceMapByType(type: LogisticsFilterType): {
    [optionId: string]: string[]
  } {
    let sourceList: string[] = []

    switch (type) {
      case LogisticsFilterType.Status:
        sourceList = [...enumToList(SitePermitStatus)]
        sourceList.push(UNASSIGNED_FILTER_OPTION)
        break
      case LogisticsFilterType.Location:
        sourceList = [
          ...this.locationAttributesStore.allLocationsWithoutEquipment.map(
            l => `${l.type}${LOCATION_SEPARATOR}${l.id}`,
          ),
          UNASSIGNED_FILTER_OPTION,
        ]
        break
      case LogisticsFilterType.Equipment:
        sourceList = [
          ...this.locationAttributesStore.offloadingEquipmentsStore.list.map(
            l => `${l.type}${LOCATION_SEPARATOR}${l.id}`,
          ),
          UNASSIGNED_FILTER_OPTION,
        ]
        break
      case LogisticsFilterType.Company:
        sourceList = [
          ...this.companiesStore.allCompaniesIds,
          UNASSIGNED_FILTER_OPTION,
        ]
        break
      case LogisticsFilterType.ResponsibleContact:
        sourceList = [
          ...this.tagsStore.tagListsByTagTypeMap[TagType.User].map(t => t.id),
          UNASSIGNED_FILTER_OPTION,
        ]
        break
      case LogisticsFilterType.Type:
        const activeTypeFacets = new Set(
          Object.entries(this.logisticsListFacetStore.facets.type)
            .filter(([, facetCount]) => facetCount.totalCount > 0)
            .map(([type]) => type),
        )
        let allTypes = this.workflowTypesStore.allTypesSortedByOrderIndex.map(
          t => t.type,
        )
        if (activeTypeFacets.size) {
          allTypes = allTypes.filter(t => activeTypeFacets.has(t))
        }
        sourceList = [...new Set(allTypes), UNASSIGNED_FILTER_OPTION]
        break
      case LogisticsFilterType.Project:
        sourceList = Object.keys(this.logisticsListFacetStore.facets.project)
        break
      case LogisticsFilterType.Category:
        sourceList = Array.from(
          new Set(
            this.workflowTypesStore.allTypes
              .map(t => t.categoryId)
              .filter(id => !!id),
          ),
        )
        sourceList.push(UNASSIGNED_FILTER_OPTION)
        break
      case LogisticsFilterType.Attachment:
        sourceList = [...AttachmentFileTypeList, UNASSIGNED_FILTER_OPTION]
        break
    }
    return sourceList.reduce((acc, optionId) => {
      acc[optionId] = []
      return acc
    }, {})
  }

  protected fieldsMap(): { [filterType: string]: UIFilterInfo } {
    return this.eventsStore.appState.workflowsFilters.fieldsMap
  }

  protected get filterTypes(): LogisticsFilterType[] {
    return formsFilterTypes
  }
}
